import * as THREE from "three";

import { createFirstLoopAudio } from "./audio/first-loop/index.ts";
import { createFarmPortfolioAudio } from "./audio/farm-portfolio/index.ts";
import type {
  FarmPortfolioCommand,
  FarmPortfolioInteractionId,
  FarmPortfolioRuntime,
  FarmPortfolioSnapshot,
} from "./gameplay/farm-portfolio/index.ts";
import {
  createFirstYardManualChainS04Port,
  createManualChainRuntime,
  projectManualChainPresentation,
  type ManualChainPresentationSnapshot,
  type ManualChainState,
  type ManualChainTickCommand,
} from "./gameplay/manual-chain/index.ts";
import { OWNER_KIND } from "./gameplay/transactions/index.ts";
import { createWalkaboutInput, type WalkaboutIntent } from "./input/walkabout/index.ts";
import { createFirstYardScene } from "./rendering/first-yard/index.ts";
import type { SunpatchRegionSceneController } from "./rendering/sunpatch-region/index.ts";
import { createFarmPortfolioHud, type FarmPortfolioChoice } from "./ui/farm-portfolio/index.ts";
import { createFirstYardHud, type FirstYardHudInputMode } from "./ui/first-yard/index.ts";
import "./setup.css";

const JOYSTICK_TRAVEL_CSS_PX = 28;
const PORTFOLIO_FIXED_STEP_MS = 50;
/** Grok/MPH feel: snappy connected-farm walk (unit tests teleport, not affected). */
const PORTFOLIO_MOVE_STEP_MILLIWU = 210;
/** Standing still near a legal station auto-works (My Perfect Hotel friction model). */
const AUTO_WORK_MOVE_DEADZONE = 0.18;
const GROK_SANDBOX = typeof location !== "undefined"
  && new URLSearchParams(location.search).get("grok") === "sandbox";

const EMPTY_COMMAND: Readonly<ManualChainTickCommand> = Object.freeze({
  interaction: null,
  routeTarget: null,
  explicitCancel: false,
  actorPresent: true,
  bodyVisible: true,
});

const app = document.querySelector<HTMLElement>("#app");
const status = document.querySelector<HTMLElement>("#boot-status");
const statusText = document.querySelector<HTMLElement>("#boot-status-text");

if (app === null || status === null || statusText === null) {
  throw new Error("Required boot surface is missing");
}
const appRoot = app;
const bootStatus = status;
const bootStatusText = statusText;

const canvas = document.createElement("canvas");
canvas.id = "game-canvas";
canvas.tabIndex = 0;
canvas.setAttribute("aria-label", "Nan's Town first-yard walkabout");
canvas.setAttribute("role", "application");
appRoot.prepend(canvas);

const context = canvas.getContext("webgl2", {
  alpha: false,
  antialias: true,
  depth: true,
  powerPreference: "high-performance",
  premultipliedAlpha: false,
  stencil: false,
});

if (context === null) {
  document.body.dataset.bootState = "webgl2-unavailable";
  bootStatusText.textContent = "This browser or graphics driver cannot provide WebGL 2.";
  bootStatus.dataset.kind = "error";
  canvas.remove();
} else {
  void bootFirstYard(context).catch(() => {
    document.body.dataset.bootState = "boot-failed";
    bootStatus.dataset.kind = "error";
    bootStatusText.textContent = "The first yard could not start. Reload to return to the farm.";
    canvas.remove();
  });
}

async function bootFirstYard(context: WebGL2RenderingContext): Promise<void> {
  const renderer = new THREE.WebGLRenderer({ canvas, context, antialias: true, alpha: false });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  // Grok juice: slightly punchier commercial-ad contrast.
  renderer.toneMappingExposure = 1.14;
  renderer.setClearColor(0xa8dde3, 1);

  const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const coarsePointerQuery = window.matchMedia("(pointer: coarse)");
  let reducedMotion = reducedMotionQuery.matches;
  let preferredInputMode: FirstYardHudInputMode = coarsePointerQuery.matches ? "touch" : "keyboard";
  let disposed = false;
  let lastEventSequence = 0;
  let lastPortfolioEventSequence = 0;
  let lastPortfolioAudioSequence = 0;
  let workHeld = false;
  let portfolioActive = false;
  let portfolioFrameMilliseconds: number | null = null;
  let portfolioAccumulatorMilliseconds = 0;
  let portfolioRequestSequence = 1;
  let portfolioBoundaryMilliseconds: number | null = null;
  let queuedPortfolioCommand: FarmPortfolioCommand | null = null;
  let portfolioActivation: Promise<void> | null = null;

  let firstYard: ReturnType<typeof createFirstYardScene> | null = createFirstYardScene({ renderer, canvas, reducedMotion });
  let region: SunpatchRegionSceneController | null = null;
  const hud = createFirstYardHud(appRoot);
  const portfolioHud = createFarmPortfolioHud(appRoot);
  portfolioHud.root.hidden = true;
  const input = createWalkaboutInput({ surface: canvas, joystick: hud.joystick });
  const audio = createFirstLoopAudio({ unlockTarget: window });
  const portfolioAudio = createFarmPortfolioAudio({ unlockTarget: window });
  let portfolioRuntime: FarmPortfolioRuntime | null = null;
  let portfolioSnapshot: FarmPortfolioSnapshot | null = null;
  let portfolioInteractionContext: typeof import("./gameplay/farm-portfolio/index.ts")["deriveFarmPortfolioInteractionContext"] | null = null;
  let portfolioViewProjection: typeof import("./ui/farm-portfolio/projection.ts")["projectFarmPortfolioView"] | null = null;
  const s04Installation = await createFirstYardManualChainS04Port();
  if (s04Installation.status !== "installed" || s04Installation.port === null) {
    document.body.dataset.bootState = "gameplay-install-failed";
    bootStatus.dataset.kind = "error";
    bootStatusText.textContent = "The first-yard action registry could not be installed. Reload to try again.";
    input.dispose();
    hud.dispose();
    portfolioHud.dispose();
    audio.dispose();
    portfolioAudio.dispose();
    firstYard?.dispose();
    renderer.dispose();
    canvas.remove();
    return;
  }
  const gameplay = createManualChainRuntime({ monotonicOriginMs: performance.now(), s04: s04Installation.port });

  const applyHudInput = (intent: Readonly<WalkaboutIntent>): void => {
    if (intent.inputMode === "keyboard") {
      preferredInputMode = "keyboard";
    } else if (intent.inputMode === "pointer") {
      preferredInputMode = coarsePointerQuery.matches ? "touch" : "pointer";
    }

    hud.setInputMode(preferredInputMode);
    const active = intent.inputMode === "pointer" && intent.magnitude > 0;
    hud.joystick.dataset.active = String(active);
    hud.joystick.dataset.axisX = intent.x.toFixed(3);
    hud.joystick.dataset.axisY = intent.z.toFixed(3);
    hud.joystick.style.setProperty("--joystick-x", `${intent.x * JOYSTICK_TRAVEL_CSS_PX}px`);
    hud.joystick.style.setProperty("--joystick-y", `${intent.z * JOYSTICK_TRAVEL_CSS_PX}px`);
  };

  const stopInputProjection = input.subscribe(applyHudInput);
  portfolioHud.setChoiceHandler((choice) => {
    if (!portfolioActive || queuedPortfolioCommand !== null) return;
    queuedPortfolioCommand = portfolioCommandForChoice(choice, portfolioRequestSequence++);
    if (queuedPortfolioCommand !== null) canvas.focus({ preventScroll: true });
  });
  applyHudInput(input.getIntent());
  hud.setReducedMotion(reducedMotion);
  portfolioHud.setReducedMotion(reducedMotion);
  projectFrame();

  const resize = (): void => {
    const width = Math.max(1, appRoot.clientWidth);
    const height = Math.max(1, appRoot.clientHeight);
    firstYard?.resize(width, height, Math.min(window.devicePixelRatio, 2));
    region?.resize(width, height, Math.min(window.devicePixelRatio, 2));
  };

  const observer = new ResizeObserver(resize);
  observer.observe(appRoot);
  resize();

  const onReducedMotionChange = (event: MediaQueryListEvent): void => {
    reducedMotion = event.matches;
    firstYard?.setReducedMotion(reducedMotion);
    region?.setReducedMotion(reducedMotion);
    hud.setReducedMotion(reducedMotion);
    portfolioHud.setReducedMotion(reducedMotion);
  };
  reducedMotionQuery.addEventListener("change", onReducedMotionChange);

  const onPointerCapabilityChange = (event: MediaQueryListEvent): void => {
    if (input.getIntent().inputMode === "none") {
      preferredInputMode = event.matches ? "touch" : "keyboard";
      hud.setInputMode(preferredInputMode);
    }
  };
  coarsePointerQuery.addEventListener("change", onPointerCapabilityChange);

  const setWorkHeld = (held: boolean): void => {
    workHeld = held;
    hud.work.dataset.active = String(held);
  };
  const onWorkPointerDown = (event: PointerEvent): void => {
    event.preventDefault();
    hud.work.setPointerCapture(event.pointerId);
    setWorkHeld(true);
  };
  const onWorkPointerEnd = (event: PointerEvent): void => {
    event.preventDefault();
    if (hud.work.hasPointerCapture(event.pointerId)) hud.work.releasePointerCapture(event.pointerId);
    setWorkHeld(false);
  };
  const onWorkKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat || !isWorkKey(event.code)) return;
    event.preventDefault();
    setWorkHeld(true);
  };
  const onWorkKeyUp = (event: KeyboardEvent): void => {
    if (!isWorkKey(event.code)) return;
    event.preventDefault();
    setWorkHeld(false);
  };
  hud.work.addEventListener("pointerdown", onWorkPointerDown);
  hud.work.addEventListener("pointerup", onWorkPointerEnd);
  hud.work.addEventListener("pointercancel", onWorkPointerEnd);
  window.addEventListener("keydown", onWorkKeyDown);
  window.addEventListener("keyup", onWorkKeyUp);
  const onCanvasPointerDown = (): void => {
    if (!coarsePointerQuery.matches) return;
    preferredInputMode = "touch";
    hud.setInputMode("touch");
  };
  canvas.addEventListener("pointerdown", onCanvasPointerDown);

  const onContextLost = (event: Event): void => {
    event.preventDefault();
    document.body.dataset.bootState = "graphics-context-lost";
    bootStatus.dataset.kind = "error";
    bootStatusText.textContent = "The farm view paused because the graphics context was lost. Reload to return to the yard.";
    renderer.setAnimationLoop(null);
    if (portfolioActive) queuedPortfolioCommand = Object.freeze({ kind: "set-paused", paused: true });
    else gameplay.handleLifecycle({ type: "pause", reason: "suspend" });
  };
  canvas.addEventListener("webglcontextlost", onContextLost);

  const onBlur = (): void => {
    setWorkHeld(false);
    if (portfolioActive) queuedPortfolioCommand = Object.freeze({ kind: "set-paused", paused: true });
    else gameplay.handleLifecycle({ type: "pause", reason: "blur" });
  };
  const onFocus = (): void => {
    if (portfolioActive) queuedPortfolioCommand = Object.freeze({ kind: "set-paused", paused: false });
    else gameplay.handleLifecycle({ type: "resume", monotonicOriginMs: performance.now() });
  };
  const onVisibilityChange = (): void => {
    if (document.visibilityState === "hidden") {
      setWorkHeld(false);
      if (portfolioActive) queuedPortfolioCommand = Object.freeze({ kind: "set-paused", paused: true });
      else gameplay.handleLifecycle({ type: "pause", reason: "visibility-hidden" });
    } else {
      if (portfolioActive) queuedPortfolioCommand = Object.freeze({ kind: "set-paused", paused: false });
      else gameplay.handleLifecycle({ type: "resume", monotonicOriginMs: performance.now() });
    }
  };
  window.addEventListener("blur", onBlur);
  window.addEventListener("focus", onFocus);
  document.addEventListener("visibilitychange", onVisibilityChange);

  const renderFrame = (frameMilliseconds: number): void => {
    if (disposed) return;
    const intent = input.getIntent();
    if (portfolioActive) {
      advancePortfolio(frameMilliseconds, intent);
      projectPortfolioFrame();
      region?.render(portfolioAccumulatorMilliseconds / PORTFOLIO_FIXED_STEP_MS, frameMilliseconds / 1000);
      const snapshot = portfolioSnapshot;
      if (snapshot === null) return;
      const audioEvents = snapshot.state.events.filter((event) => event.sequence > lastPortfolioAudioSequence);
      if (audioEvents.length > 0) {
        lastPortfolioAudioSequence = audioEvents.at(-1)!.sequence;
        portfolioAudio.consume(audioEvents);
      }
      return;
    }
    gameplay.submitHeldInput(normalizedGameplayInput(intent));
    // MPH parity: stand still in a station to work; Space/E remains optional boost/clarity.
    const autoWork = intent.magnitude <= AUTO_WORK_MOVE_DEADZONE;
    gameplay.issueCommand(commandFor(gameplay.snapshot.state, intent, workHeld || autoWork));
    gameplay.advanceFrame(frameMilliseconds);
    const presentation = projectFrame();
    firstYard?.render(0, frameMilliseconds / 1000);
    const newEvents = presentation.events.filter((event) => event.sequence > lastEventSequence);
    if (newEvents.length > 0) {
      lastEventSequence = newEvents.at(-1)!.sequence;
      audio.consume(newEvents);
    }
    if (presentation.objectiveId === "pen-extension-ready") {
      portfolioBoundaryMilliseconds ??= frameMilliseconds;
      if (intent.magnitude > 0 || frameMilliseconds - portfolioBoundaryMilliseconds >= 1_200) void activatePortfolio(frameMilliseconds);
    }
  };

  renderer.setAnimationLoop(renderFrame);
  document.body.dataset.bootState = "ready";
  document.body.dataset.threeRevision = THREE.REVISION;
  bootStatus.removeAttribute("data-kind");
  bootStatusText.textContent = "Sunpatch Yard · Walk into corn to harvest";
  canvas.focus({ preventScroll: true });

  const dispose = (): void => {
    if (disposed) return;
    disposed = true;
    renderer.setAnimationLoop(null);
    observer.disconnect();
    reducedMotionQuery.removeEventListener("change", onReducedMotionChange);
    coarsePointerQuery.removeEventListener("change", onPointerCapabilityChange);
    canvas.removeEventListener("webglcontextlost", onContextLost);
    window.removeEventListener("blur", onBlur);
    window.removeEventListener("focus", onFocus);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    hud.work.removeEventListener("pointerdown", onWorkPointerDown);
    hud.work.removeEventListener("pointerup", onWorkPointerEnd);
    hud.work.removeEventListener("pointercancel", onWorkPointerEnd);
    window.removeEventListener("keydown", onWorkKeyDown);
    window.removeEventListener("keyup", onWorkKeyUp);
    canvas.removeEventListener("pointerdown", onCanvasPointerDown);
    stopInputProjection();
    input.dispose();
    audio.dispose();
    portfolioAudio.dispose();
    hud.dispose();
    portfolioHud.dispose();
    firstYard?.dispose();
    region?.dispose();
    renderer.dispose();
    canvas.remove();
  };

  if (import.meta.hot !== undefined) {
    import.meta.hot.dispose(dispose);
  }

  function projectFrame(): ManualChainPresentationSnapshot {
    const presentation = projectManualChainPresentation(gameplay.snapshot, reducedMotion);
    firstYard?.projectManualChain(presentation, presentation.events);
    hud.setSnapshot({
      position: presentation.player.position,
      facing: presentation.player.facingRadians,
      moving: presentation.player.moving,
      objectiveId: presentation.objectiveId,
      cargo: presentation.cargo,
      cash: presentation.cash,
    });
    document.body.dataset.gameplayTick = String(presentation.tick);
    document.body.dataset.gameplayHash = presentation.gameplayHash;
    document.body.dataset.manualObjective = presentation.objectiveId;
    document.body.dataset.penExtension = presentation.pad.state;
    document.body.dataset.playerX = presentation.player.position.x.toFixed(3);
    document.body.dataset.playerZ = presentation.player.position.z.toFixed(3);
    document.body.dataset.cargoKind = presentation.cargo.kind;
    document.body.dataset.cargoCount = String(presentation.cargo.count);
    document.body.dataset.cash = String(presentation.cash);
    return presentation;
  }

  async function activatePortfolio(frameMilliseconds: number): Promise<void> {
    if (portfolioActive) return;
    if (portfolioActivation !== null) return portfolioActivation;
    document.body.dataset.portfolioState = "loading";
    bootStatusText.textContent = "Opening the connected farm…";
    portfolioActivation = Promise.all([
      import("./gameplay/farm-portfolio/index.ts"),
      import("./rendering/sunpatch-region/index.ts"),
      import("./ui/farm-portfolio/projection.ts"),
    ]).then(([portfolioModule, { createSunpatchRegionScene }, projectionModule]) => {
      if (disposed || portfolioActive) return;
      portfolioRuntime = portfolioModule.createFarmPortfolioRuntime();
      portfolioSnapshot = GROK_SANDBOX
        ? portfolioModule.snapshotFarmPortfolioState({
          ...portfolioRuntime.initialSnapshot.state,
          cash: 5_000,
        })
        : portfolioRuntime.initialSnapshot;
      portfolioInteractionContext = portfolioModule.deriveFarmPortfolioInteractionContext;
      portfolioViewProjection = projectionModule.projectFarmPortfolioView;
      portfolioActive = true;
      portfolioFrameMilliseconds = frameMilliseconds;
      portfolioAccumulatorMilliseconds = 0;
      region = createSunpatchRegionScene({ renderer, canvas, reducedMotion });
      firstYard?.dispose();
      firstYard = null;
      hud.root.dataset.portfolioMode = "true";
      if (coarsePointerQuery.matches) {
        preferredInputMode = "touch";
        hud.setInputMode("touch");
      }
      portfolioHud.root.hidden = false;
      document.body.dataset.portfolioState = "active";
      document.body.dataset.grokFork = "true";
      document.body.dataset.grokSandbox = String(GROK_SANDBOX);
      bootStatusText.textContent = GROK_SANDBOX
        ? "Grok sandbox · 5000 Cash · explore all 8 businesses"
        : "Sunpatch Farm is open · Choose your first investment";
      resize();
      projectPortfolioFrame();
    }).catch(() => {
      if (disposed) return;
      document.body.dataset.portfolioState = "load-failed";
      bootStatus.dataset.kind = "error";
      bootStatusText.textContent = "The connected farm could not open. Reload to return to Home Acre.";
    });
    await portfolioActivation;
  }

  function advancePortfolio(frameMilliseconds: number, intent: Readonly<WalkaboutIntent>): void {
    const runtime = portfolioRuntime;
    let snapshot = portfolioSnapshot;
    const interactionContext = portfolioInteractionContext;
    if (runtime === null || snapshot === null || interactionContext === null) return;
    if (portfolioFrameMilliseconds === null) {
      portfolioFrameMilliseconds = frameMilliseconds;
      return;
    }
    const elapsed = Math.max(0, Math.min(250, frameMilliseconds - portfolioFrameMilliseconds));
    portfolioFrameMilliseconds = frameMilliseconds;
    portfolioAccumulatorMilliseconds += elapsed;
    let firstStep = true;
    while (portfolioAccumulatorMilliseconds >= PORTFOLIO_FIXED_STEP_MS) {
      portfolioAccumulatorMilliseconds -= PORTFOLIO_FIXED_STEP_MS;
      const command = firstStep && queuedPortfolioCommand !== null
        ? queuedPortfolioCommand
        : portfolioCommandForFrame(snapshot, intent, workHeld, portfolioRequestSequence++, interactionContext);
      queuedPortfolioCommand = null;
      snapshot = runtime.step(snapshot, Object.freeze({
        tick: snapshot.state.tick + 1,
        command,
      }));
      portfolioSnapshot = snapshot;
      firstStep = false;
    }
  }

  function projectPortfolioFrame() {
    const snapshot = portfolioSnapshot;
    const projectView = portfolioViewProjection;
    if (snapshot === null || projectView === null) return null;
    const view = projectView(snapshot, reducedMotion, lastPortfolioEventSequence);
    region?.project(view.region, view.events);
    portfolioHud.setSnapshot(view.hud);
    if (view.events.length > 0) lastPortfolioEventSequence = snapshot.state.events.at(-1)?.sequence ?? lastPortfolioEventSequence;
    if (view.hud.estateComplete) {
      document.body.dataset.estateComplete = "true";
      bootStatusText.textContent = "Estate thriving · all 8 businesses sold!";
    }
    document.body.dataset.gameplayTick = String(snapshot.state.tick);
    document.body.dataset.gameplayHash = snapshot.gameplayHash;
    document.body.dataset.portfolioObjective = view.hud.objectiveLabel;
    document.body.dataset.portfolioDistrict = view.hud.districtId;
    document.body.dataset.estateProgress = `${view.hud.lineProgress.filter((entry) => entry.sold).length}/8`;
    document.body.dataset.playerX = view.region.player.position.x.toFixed(3);
    document.body.dataset.playerZ = view.region.player.position.z.toFixed(3);
    document.body.dataset.cash = String(snapshot.state.cash);
    document.body.dataset.cargoKind = view.hud.cargo.family;
    document.body.dataset.cargoCount = String(view.hud.cargo.count);
    document.body.dataset.portfolioHand = view.hud.hand.hired ? view.hud.hand.jobId ?? "idle" : "not-hired";
    document.body.dataset.portfolioAction = view.region.currentAction?.kind ?? "none";
    document.body.dataset.grainState = view.region.s03?.grain.cropState ?? "unavailable";
    document.body.dataset.grainThreshState = view.region.s03?.grain.threshState ?? "unavailable";
    document.body.dataset.grainMillState = view.region.s03?.grain.millState ?? "unavailable";
    document.body.dataset.hayState = view.region.s03?.hay.fieldState ?? "unavailable";
    document.body.dataset.hayBalerState = view.region.s03?.hay.balerState ?? "unavailable";
    document.body.dataset.dairyState = view.region.s03?.dairy.cowState ?? "unavailable";
    document.body.dataset.dairyCreameryState = view.region.s03?.dairy.creameryState ?? "unavailable";
    document.body.dataset.apiaryState = view.region.s04?.apiary.hiveState ?? "unavailable";
    document.body.dataset.honeyHouseState = view.region.s04?.apiary.extractorState ?? "unavailable";
    document.body.dataset.sheepState = view.region.s04?.sheep.sheepState ?? "unavailable";
    document.body.dataset.fiberShedState = view.region.s04?.sheep.fiberState ?? "unavailable";
    document.body.dataset.craftTableState = view.region.s04?.craft.tableState ?? "unavailable";
    return view;
  }
}

function portfolioCommandForChoice(choice: Readonly<FarmPortfolioChoice>, sequence: number): FarmPortfolioCommand | null {
  if (choice.kind === "investment" && choice.investmentId !== null) {
    return Object.freeze({
      kind: "purchase-investment",
      actorId: "player",
      investmentId: choice.investmentId,
      requestId: `portfolio-purchase-${choice.investmentId}-${sequence}`,
    });
  }
  if (choice.kind === "hire") {
    return Object.freeze({
      kind: "purchase-investment",
      actorId: "player",
      investmentId: "bunkhouse-generalist",
      requestId: `portfolio-hire-${sequence}`,
    });
  }
  if (choice.kind === "job" && choice.lineId !== null && choice.jobId !== null) {
    return Object.freeze({
      kind: "assign-hand",
      actorId: "player",
      lineId: choice.lineId,
      duty: choice.jobId,
      requestId: `portfolio-assign-${choice.lineId}-${choice.jobId}-${sequence}`,
    });
  }
  if (choice.kind === "craft-cancel" && choice.slotIndex !== undefined) {
    return Object.freeze({
      kind: "cancel-craft-slot",
      actorId: "player",
      slotIndex: choice.slotIndex,
      requestId: `portfolio-craft-cancel-${choice.slotIndex}-${sequence}`,
    });
  }
  return null;
}

function portfolioCommandForFrame(
  snapshot: Readonly<FarmPortfolioSnapshot>,
  intent: Readonly<WalkaboutIntent>,
  workHeld: boolean,
  sequence: number,
  deriveInteractionContext: typeof import("./gameplay/farm-portfolio/index.ts")["deriveFarmPortfolioInteractionContext"],
): FarmPortfolioCommand | null {
  const x = axisSign(intent.x);
  const z = axisSign(intent.z);
  if (x !== 0 || z !== 0) {
    return Object.freeze({
      kind: "move-actor",
      actorId: "player",
      deltaXMilliWU: x * PORTFOLIO_MOVE_STEP_MILLIWU,
      deltaZMilliWU: z * PORTFOLIO_MOVE_STEP_MILLIWU,
    });
  }
  // Idle = auto-work (MPH). Explicit Work button still works the same path.
  const autoWork = intent.magnitude <= AUTO_WORK_MOVE_DEADZONE;
  if (!workHeld && !autoWork) return null;
  const player = snapshot.state.actors.find((candidate) => candidate.actorId === "player")!;
  if (player.activeActionId !== null) {
    const action = snapshot.state.activeActions.find((candidate) => candidate.actionId === player.activeActionId);
    if (action !== undefined && snapshot.state.tick >= action.commitEligibleTick) {
      return Object.freeze({ kind: "commit-action", actorId: "player", actionId: action.actionId });
    }
    return null;
  }
  const context = deriveInteractionContext(snapshot, "player");
  const interactionId = choosePortfolioInteraction(context.availableInteractions);
  return interactionId === null ? null : Object.freeze({
    kind: "begin-action",
    actorId: "player",
    interactionId,
    requestId: `portfolio-action-${interactionId}-${sequence}`,
  });
}

function choosePortfolioInteraction(available: readonly FarmPortfolioInteractionId[]): FarmPortfolioInteractionId | null {
  // Prefer cash-out and collect first (MPH: finish the juicy conversion before new harvests).
  const order: readonly FarmPortfolioInteractionId[] = Object.freeze([
    "sell-cargo",
    "pack-craft-slot-0", "pack-craft-slot-1",
    "collect-produce-crates", "collect-juice-cases", "collect-pomace", "collect-eggs",
    "collect-flour-sacks", "collect-bran-portions", "collect-threshed-wheat", "collect-hay-bales",
    "collect-cheese-wheels", "collect-whey-portions",
    "collect-honey-jars", "collect-wax-cakes", "collect-yarn-bolts",
    "wash-grade-pack", "press-bottle-fruit", "mill-grain", "thresh-grain", "bale-hay", "turn-hay", "press-cheese", "tend-poultry",
    "extract-honey-wax", "process-fiber",
    "stage-gristmill", "stage-grain-thresh", "stage-hay-ration", "stage-hay", "stage-dairy",
    "stage-apiary", "stage-sheep", "stage-craft-slot-0", "stage-craft-slot-1",
    "stage-garden", "stage-orchard", "stage-poultry",
    "feed-tend-cows-cheese", "feed-tend-cows-fresh", "milk-cows-cheese", "milk-cows-fresh",
    "feed-tend-sheep-refined", "feed-tend-sheep-raw", "shear-sheep-refined", "shear-sheep-raw",
    "tend-apiary", "harvest-comb",
    "harvest-garden-refined", "harvest-garden-raw", "pick-orchard-pressed", "pick-orchard-fresh",
    "harvest-grain-refined", "harvest-grain-raw", "mow-rake-hay",
    "harvest-corn",
  ]);
  return order.find((candidate) => available.includes(candidate)) ?? null;
}

function axisSign(value: number): -1 | 0 | 1 {
  return value < -0.25 ? -1 : value > 0.25 ? 1 : 0;
}

function normalizedGameplayInput(intent: Readonly<WalkaboutIntent>) {
  const axis = (value: number): -1 | 0 | 1 => value < -0.25 ? -1 : value > 0.25 ? 1 : 0;
  return Object.freeze({
    type: "normalized-held-input" as const,
    movementX: axis(intent.x),
    movementZ: axis(intent.z),
    padCharge: false,
  });
}

function commandFor(
  state: Readonly<ManualChainState>,
  intent: Readonly<WalkaboutIntent>,
  workHeld: boolean,
): Readonly<ManualChainTickCommand> {
  // Moving cancels work. Standing still (caller ORs Work hold with auto-work) starts the legal station job.
  if (intent.magnitude > AUTO_WORK_MOVE_DEADZONE || !workHeld) return EMPTY_COMMAND;
  if (state.activeAction !== null) {
    return Object.freeze({ ...EMPTY_COMMAND, interaction: state.activeAction.verb });
  }
  if (state.objectiveId === "pick-corn") {
    return Object.freeze({ ...EMPTY_COMMAND, interaction: "harvest" });
  }
  if (state.objectiveId === "feed-chickens") {
    const nearPen = squaredDistance(state.player, 1_000_000, -1_473_000) <= 1_000_000 ** 2;
    const carriesCorn = state.ledger.items.some((item) =>
      item.ownerKind === OWNER_KIND.ACTOR_CARGO && item.ownerId === 1 && item.kind === 1
    );
    return Object.freeze({
      ...EMPTY_COMMAND,
      interaction: nearPen ? (carriesCorn ? "stage" : "tend") : null,
    });
  }
  if (state.objectiveId === "collect-eggs") {
    return Object.freeze({ ...EMPTY_COMMAND, interaction: "collect" });
  }
  if (state.objectiveId === "sell-eggs") {
    const nearMarket = squaredDistance(state.player, -1_551_000, 2_945_000) <= 1_000_000 ** 2;
    return Object.freeze({ ...EMPTY_COMMAND, interaction: nearMarket ? "sale" : null });
  }
  // After first sale / pad phase: still auto-try nearest legal chain step when standing still.
  if (state.objectiveId === "pen-extension-ready") {
    const carriesCorn = state.ledger.items.some((item) =>
      item.ownerKind === OWNER_KIND.ACTOR_CARGO && item.ownerId === 1 && item.kind === 1
    );
    const carriesEgg = state.ledger.items.some((item) =>
      item.ownerKind === OWNER_KIND.ACTOR_CARGO && item.ownerId === 1 && item.kind === 2
    );
    const nearPen = squaredDistance(state.player, 1_000_000, -1_473_000) <= 1_000_000 ** 2;
    const nearMarket = squaredDistance(state.player, -1_551_000, 2_945_000) <= 1_000_000 ** 2;
    if (nearMarket && carriesEgg) return Object.freeze({ ...EMPTY_COMMAND, interaction: "sale" });
    if (nearPen && carriesCorn) return Object.freeze({ ...EMPTY_COMMAND, interaction: "stage" });
    if (nearPen && !carriesCorn) return Object.freeze({ ...EMPTY_COMMAND, interaction: "collect" });
    return Object.freeze({ ...EMPTY_COMMAND, interaction: "harvest" });
  }
  return EMPTY_COMMAND;
}

function isWorkKey(code: string): boolean {
  return code === "Space" || code === "Enter" || code === "KeyE";
}

function squaredDistance(
  point: Readonly<{ xMicroWU: number; zMicroWU: number }>,
  xMicroWU: number,
  zMicroWU: number,
): number {
  const dx = point.xMicroWU - xMicroWU;
  const dz = point.zMicroWU - zMicroWU;
  return dx * dx + dz * dz;
}
